import React, { useContext, useEffect, useState } from "react";
import { Button, TextField, Grid } from "@material-ui/core";
import exportFromJSON from "export-from-json";
import { useLazyQuery, useMutation } from "@apollo/client";
import moment from "moment";

import logger from "../../utils/logger";
import ForecastTable from "../forecast-table/forecast-table.component";
import { MatchContext, UserInfo } from "../../context";
import { APP_INSIGHTS_EVENTS, FORECAST_TABLE_PARAMS } from "../../constants";
import {
  SET_ACTIVE_FORECAST,
  SET_DATA,
  SET_EMPTY_FORECAST,
} from "../../reduce/actionTypes";
import {
  SEND_CREATE_FORECAST,
  SEND_UPDATE_FORECAST,
} from "../../mutations/table-data.gql";
import {
  GET_ACTIVE_FORECAST,
  GET_LIST_OF_FORECASTS_MONTHS,
} from "../../queries/stockpiles.gql";
import LoaderBar from "../common/loader-bar.component";
import ForecastMonthsPaginator from "../forecast-months-paginator/forecast-months-paginator.component";
import { prepareDataForExport } from "../../utils/prepare-table-average-data";
import ForecastNotes from "../forecast-notes/forecast-notes.component";
import ForecastUpdatesContainer from "../forecast-updates-container/forecast-updates-container.component";
import IntraTableMessage from "../HOC/intratable-message/intratable-message.component";
import ErrorMessage from "../common/error-message.component";
import useTrackEvent from "../../utils/hooks/use-track-app-event";

export const ForecastingPage = (): JSX.Element => {
  const trackEvent = useTrackEvent();
  const { state, dispatch } = useContext(MatchContext);
  const { userId, username } = useContext(UserInfo);
  const [createForecast, { data: created, error: createForecastError }] =
    useMutation(SEND_CREATE_FORECAST);
  const [updateForecast, { data: updated, error: updateForecastError }] =
    useMutation(SEND_UPDATE_FORECAST);

  const [month, setMonth] = useState(moment().format("YYYY-MM"));
  const [monthToSend, setMonthToSend] = useState(moment().format("YYYY-MM"));
  const [months, setMonths] = useState([]);
  const [creatingNew, setCreatingNew] = useState(false);
  const [error, setError] = useState(false);

  const [getListOfMonths, { data: monthsData, error: getMonthsListError }] =
    useLazyQuery(GET_LIST_OF_FORECASTS_MONTHS, { fetchPolicy: "no-cache" });
  const [
    getActiveForecast,
    { data: activeData, loading, error: getActiveForecastError },
  ] = useLazyQuery(GET_ACTIVE_FORECAST, { fetchPolicy: "no-cache" });

  useEffect(() => {
    getActiveForecast({
      variables: {
        monthId: moment().format("YYYY-MM"),
      },
    });
    getListOfMonths();
  }, []);

  useEffect(() => {
    getActiveForecast({
      variables: {
        monthId: moment(month).format("YYYY-MM"),
      },
    });
  }, [month]);

  useEffect(() => {
    if (
      createForecastError ||
      updateForecastError ||
      getMonthsListError ||
      getActiveForecastError
    ) {
      setError(true);
      if (createForecastError)
        logger.error("Create forecast error:", createForecastError);
      if (updateForecastError)
        logger.error("Update forecast error:", updateForecastError);
      if (getActiveForecastError)
        logger.error("Get active forecast error:", getActiveForecastError);
      if (getMonthsListError)
        logger.error("Get months list error:", getMonthsListError);
    }
  }, [
    createForecastError,
    updateForecastError,
    getMonthsListError,
    getActiveForecastError,
  ]);

  useEffect(() => {
    if (
      monthsData?.getListOfAvailableMonthsWithTargets?.availableTargetMonths
    ) {
      setMonths(
        monthsData.getListOfAvailableMonthsWithTargets.availableTargetMonths
      );
    }
  }, [monthsData]);

  useEffect(() => {
    if (activeData?.getActiveForecast?.monthId) {
      setMonth(activeData.getActiveForecast.monthId);
      setMonthToSend(activeData.getActiveForecast.monthId);
    }
  }, [activeData]);

  useEffect(() => {
    getListOfMonths();
  }, [created, updated]);

  const handleExport = () => {
    const forecastData = JSON.parse(JSON.stringify(state.forecastTableRows));
    const fileName = `forecast-${moment().format("YYYY-MM-DD h:mm")}`;
    const exportType = exportFromJSON.types.xls;
    const data = prepareDataForExport(forecastData);

    try {
      exportFromJSON({ data, fileName, exportType });
    } catch (err) {
      logger.error("Export targets error");
    }

    trackEvent(APP_INSIGHTS_EVENTS.EXPORT_FORECAST);
  };

  const handleSaveUpdateForecast = () => {
    const forecastData: any = {
      notes: state.forecastNotes,
      userInfo: { userId, username },
      includedStockpiles: state.forecastTableRows,
      monthId: monthToSend,
    };

    if (state.forecastId && state.forecastRequestId) {
      forecastData.forecastId = state.forecastId;
      forecastData.id = state.forecastRequestId;

      for (let forcastRow of forecastData.includedStockpiles) {
        if (forcastRow.__typename) delete forcastRow.__typename;
      }

      logger.info(`Update forecast: ${forecastData.forecastId}`, {
        ...forecastData,
      });

      updateForecast({
        variables: { ...forecastData },
      });

      dispatch({
        type: SET_ACTIVE_FORECAST,
        payload: {
          forecastTableRows: JSON.stringify(state.forecastTableRows),
          forecastId: state.forecastId,
          forecastRequestId: state.forecastRequestId,
          forecastNotes: state.forecastNotes,
        },
      });
    } else {
      logger.info(`Create forecast`, { ...forecastData });

      for (let forcastRow of forecastData.includedStockpiles) {
        if (forcastRow.__typename) delete forcastRow.__typename;
      }

      createForecast({
        variables: { ...forecastData },
      }).then((r) =>
        dispatch({
          type: SET_ACTIVE_FORECAST,
          payload: {
            forecastTableRows: JSON.stringify(state.forecastTableRows),
            forecastId: state.forecastId,
            forecastRequestId: state.forecastRequestId,
            forecastNotes: state.forecastNotes,
          },
        })
      );
    }

    trackEvent(APP_INSIGHTS_EVENTS.CLICK_SAVE_FORECAST, {
      forecastTableRows: JSON.stringify(state.forecastTableRows),
      forecastId: state.forecastId,
      forecastRequestId: state.forecastRequestId,
      forecastNotes: state.forecastNotes,
    });

    setMonth(forecastData.monthId);
    setCreatingNew(false);
  };

  const handleCreateNewForecast = () => {
    setCreatingNew(true);
    dispatch({
      type: SET_EMPTY_FORECAST,
    });
  };

  const handleDateChange = (e) => {
    setMonthToSend(e.target.value);
  };

  const handleSelectMonths = (item: string) => {
    if (months.includes(item)) {
      setMonth(item);
      setMonthToSend(item);
    }
    setCreatingNew(false);
  };

  const handleCancelForecast = () => {
    setCreatingNew(false);
    if (state.forecastTableRowsPrevious.length) {
      dispatch({
        type: SET_DATA,
        payload: {
          forecastTableRows: JSON.parse(
            JSON.stringify(state.forecastTableRowsPrevious)
          ),
          forecastId: state.forecastIdPrevious,
          forecastRequestId: state.forecastRequestIdPrevious,
          forecastNotes: state.forecastNotesPrevious,
        },
      });
    }
  };

  // check if all features with positive weight have inputs
  const checkFilledInputs = () => {
    let filled: any = [];

    for (let row of state.forecastTableRows) {
      Object.keys(FORECAST_TABLE_PARAMS).forEach((item) => {
        if (row[item]) filled.push(true);
        else filled.push(false);
      });
    }

    filled = filled.filter((item) => item !== true);

    return filled.length === 0;
  };

  const checkClickedNew = () => {
    return (
      state.forecastTableRows.length === 1 &&
      Object.values(state.forecastTableRows[0]).length === 1 &&
      !creatingNew
    );
  };

  const checkAlreadyExist = () => {
    return (
      months.includes(monthToSend) && (creatingNew || month !== monthToSend)
    );
  };

  const checkReadyForSave = () => {
    if (
      JSON.stringify(state.forecastTableRows) ===
        JSON.stringify(state.forecastTableRowsPrevious) &&
      JSON.stringify(state.forecastNotes) ===
        JSON.stringify(state.forecastNotesPrevious) &&
      month === monthToSend
    ) {
      return dispatch({
        type: SET_DATA,
        payload: { forecastReadyForSave: false },
      });
    }

    if (
      Object.values(state.forecastInputErrors).filter((item) => item !== null)
        .length
    ) {
      return dispatch({
        type: SET_DATA,
        payload: { forecastReadyForSave: false },
      });
    }

    if (!checkFilledInputs()) {
      return dispatch({
        type: SET_DATA,
        payload: { forecastReadyForSave: false },
      });
    }

    return dispatch({
      type: SET_DATA,
      payload: { forecastReadyForSave: true },
    });
  };

  useEffect(() => {
    checkReadyForSave();
  }, [
    state.forecastInputErrors,
    state.forecastTableRows,
    state.forecastNotes,
    month,
    monthToSend,
  ]);

  return (
    <div className="cards">
      <div className="custom-card card-common root">
        <div className="header-content">
          <Grid container item xs={12} alignItems="center">
            <Grid container item xs={9} justifyContent="flex-end">
              <Grid item xs={1}>
                <div className="bottom-align item-container">
                  <Button
                    variant="contained"
                    color="primary"
                    className="stick-right mr-1r"
                    onClick={handleCreateNewForecast}
                    disabled={checkClickedNew()}
                  >
                    New
                  </Button>
                </div>
              </Grid>
              <Grid container item xs={3}>
                {activeData && (
                  <div className="mr-2r item-container date-container">
                    {moment(month) > moment() || creatingNew ? (
                      <React.Fragment>
                        <TextField
                          id="forecast-month"
                          value={monthToSend}
                          onChange={handleDateChange}
                          type="month"
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                        {checkAlreadyExist() && (
                          <div className="exist">Already exists</div>
                        )}
                      </React.Fragment>
                    ) : (
                      <span>{moment(month).format("MMMM YYYY")}</span>
                    )}
                  </div>
                )}
              </Grid>
              <Grid
                container
                item
                xs={8}
                justifyContent="flex-start"
                className={creatingNew && "disabled"}
              >
                {activeData && months && month && (
                  <ForecastMonthsPaginator
                    months={months}
                    month={month}
                    handleSelectMonths={handleSelectMonths}
                  />
                )}
              </Grid>
            </Grid>
            <Grid container item xs={3} justifyContent="flex-end">
              <Grid item>
                <div className="item-container bottom-align stick-right">
                  <Button
                    variant="contained"
                    color="primary"
                    className="stick-right mr-1r"
                    onClick={handleSaveUpdateForecast}
                    disabled={!state.forecastReadyForSave}
                  >
                    Save
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    className="stick-right"
                    onClick={handleCancelForecast}
                    disabled={
                      JSON.stringify(state.forecastTableRows) ===
                        JSON.stringify(state.forecastTableRowsPrevious) &&
                      Object.values(state.forecastTableRows[0]).length !== 1
                    }
                  >
                    Cancel changes
                  </Button>
                </div>
              </Grid>
            </Grid>
          </Grid>
        </div>
        <div className="content">
          <Grid container item xs={12}>
            <Grid item xs={9}>
              <div className="table-container">
                {loading ? (
                  <LoaderBar />
                ) : activeData ? (
                  <div
                    className={
                      new Date(moment(month).format("YYYY-MM")) <
                        new Date(moment().format("YYYY-MM")) &&
                      !creatingNew &&
                      "disabled"
                    }
                  >
                    <ForecastTable activeData={activeData} />
                  </div>
                ) : (
                  <IntraTableMessage>
                    <ErrorMessage
                      error={{}}
                      customMessage={"No forecast data"}
                    ></ErrorMessage>
                  </IntraTableMessage>
                )}
                {error && (
                  <ErrorMessage
                    error={
                      createForecastError ||
                      updateForecastError ||
                      getMonthsListError ||
                      getActiveForecastError
                    }
                  />
                )}
                <Grid container item xs={12}>
                  <Grid item xs={9}>
                    {!creatingNew && (
                      <ForecastUpdatesContainer activeData={activeData} />
                    )}
                  </Grid>
                  <Grid container item xs={3} justifyContent="flex-end">
                    <Grid item>
                      <Button
                        variant="outlined"
                        color="primary"
                        className="export"
                        onClick={handleExport}
                        disabled={
                          JSON.stringify(state.forecastTableRows) !==
                          JSON.stringify(state.forecastTableRowsPrevious)
                        }
                      >
                        Export to excel
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </div>
            </Grid>
            <Grid item xs={3}>
              <ForecastNotes month={month} creatingNew={creatingNew} />
            </Grid>
          </Grid>
        </div>
      </div>
    </div>
  );
};
